import { AlphaHttpClient, ProjectService } from '@alpha-core/platform-ui';
import { Injectable, inject } from '@angular/core';
import * as Handlebars from 'handlebars';
import { BehaviorSubject, Observable, ReplaySubject, mergeMap, of } from 'rxjs';

export interface ILanguageConfig {
    id: number;
    translations: ILanguage[];
    version: string;
}
export interface ILanguage {
    languageName: string;
    languageKey: string;
    id: number;
    isDefault: boolean;
    dir: 'rtl' | 'ltr';
    langTranslations: Record<string, string>;
}

declare var alpha: any;
@Injectable({ providedIn: 'root' })
export class LanguagesService {
    private httpClient = inject(AlphaHttpClient);
    private projectService = inject(ProjectService);

    private selectedLanguage: ILanguage | undefined;
    public languageList: ILanguage[] | null = null;
    private selectedLanguageSubject = new BehaviorSubject<ILanguage | null>(null);
    private defaultLanguage?: ILanguage;
    private translations: Record<string, string> = {};
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };

    public selectedLanguage$: Observable<ILanguage | null>;
    private languageReadySubject = new ReplaySubject<void>(1);

    constructor() {
        this.selectedLanguage$ = this.selectedLanguageSubject.asObservable();
    }

    getLanguageReady$(): Observable<void> {
        return this.languageReadySubject.asObservable(); // Use this in components to wait for languages to be ready
    }

    fetchLanguages() {
        if (this.languageList && Array.isArray(this.languageList)) {
            return of(this.languageList);
        }
        const project = this.projectService.fetchCurrentProject();
        const projectId = project?.projectId;
        const getCurrentTag = project?.currentTag ? true : false;
        return this.httpClient
            .post<ILanguageConfig>(this.domainUrl, `/language-translation/fetch-languages`, {
                projectId,
                versionConfig: {
                    getCurrentTag,
                },
            })
            .pipe(
                mergeMap(async (languages) => {
                    if (languages.translations.length) {
                        this.languageList = languages.translations;
                        const defaultLanguage =
                            languages.translations.find((lang) => lang.isDefault) || languages.translations[0];

                        this.defaultLanguage = defaultLanguage;
                        await this.addlangaugeInLocalStorage();
                        this.languageReadySubject.next();
                    }
                    return languages.translations;
                }),
            );
    }

    async setLanguage(language: ILanguage) {
        this.selectedLanguage = language;
        localStorage.setItem('currentLanguage', JSON.stringify(language));
        window.alpha['project']['langTranslations'] = JSON.stringify(language.langTranslations || {});
        this.translations = language.langTranslations;
        this.selectedLanguageSubject.next(language);
        window.dispatchEvent(new Event('page_language_change'));
    }

    getLanguage(): ILanguage {
        const currentLang = localStorage.getItem('currentLanguage');
        return currentLang ? JSON.parse(currentLang) : this.selectedLanguage;
    }

    getLanguageList() {
        if (this.languageList?.length) {
            return of(this.languageList);
        } else {
            return this.fetchLanguages();
        }
    }

    async addlangaugeInLocalStorage() {
        const currentLocalLanguage = localStorage.getItem('currentLanguage');
        if (currentLocalLanguage) {
            const languageKey = JSON.parse(currentLocalLanguage).languageKey;
            const currLangauge = this.languageList?.find((lang) => lang.languageKey === languageKey);
            if (currLangauge) {
                await this.setLanguage(currLangauge);
                return;
            }
        }
        if (this.defaultLanguage) this.setLanguage(this.defaultLanguage);
    }

    fetchLanguageTranslations() {
        const { languageName, languageKey } = this.getLanguage();
        const project = this.projectService.fetchCurrentProject();
        const getCurrentTag = project.currentTag ? true : false;
        const projectId = project?.projectId;
        return this.httpClient.post<Record<string, string>>(
            this.domainUrl,
            '/language-translation/fetch-translations',
            {
                projectId,
                languageKey,
                languageName,
                versionConfig: {
                    getCurrentTag,
                },
            },
        );
    }
    clearLanguage() {
        localStorage.removeItem('currentLanguage');
    }

    translateConfig(config: any) {
        const translations = {
            lang: JSON.parse(window.alpha['project']['langTranslations'] || '{}'),
            co: alpha?.co?.get() || {},
        };
        return this.translate(config, translations);
    }

    translate(
        config: any,
        translations: { lang: { [key: string]: string }; co: { [key: string]: any }; [key: string]: any },
    ): any {
        if (config === null || config === undefined) {
            return config;
        }

        try {
            const template = Handlebars.compile(JSON.stringify(config));
            return JSON.parse(template(translations));
        } catch (error) {
            console.log('Failed to translate -->', error);
            return config;
        }
    }

    getTranslation(key: string) {
        return this.translations[key] || null;
    }

    private translateCache = new Map<string, string>();
    translateInboxLabel(label: string): string {
        if (!label) return '';
        const key = label + this.selectedLanguage?.languageName;
        if (this.translateCache.has(key)) {
            return this.translateCache.get(key)!;
        }

        const translated = this.translateConfig(label);
        this.translateCache.set(key, translated);

        return translated;
    }
}
